import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  Divider,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';

import { setDiscoverSort } from 'features/discoverFiltersSlice';
import ChevronDownIcon from 'public/icons/chevron-down.svg';
import ChevronUpIcon from 'public/icons/chevron-up.svg';

const useStyles = makeStyles({
  root: {
    boxShadow: 'none',
    minWidth: 'fit-content',
  },

  paper: {
    borderRadius: 10,
    boxShadow: '0 0 30px 0 rgba(0, 0, 0, 0.1)',
  },

  menuList: {
    padding: '12px 10px',
  },

  menuListItem: {
    padding: '5px 5px',
    margin: '3px 0',
    fontFamily: 'Avenir Heavy',
    fontSize: 14,
    color: '#323643',

    '&:hover': {
      backgroundColor: '#e8fefc',
      borderRadius: 5,
      color: '#323643',
    },
  },
});

const spanStyle = { marginRight: 10, color: '#323643', height: 24 };
const marginRight = { marginRight: 10, color: '#323643' };
const noPaddingRight = { noPaddingRight: 0 };
const zIndex = { zIndex: 11 };
const iconStyle = {
  filter:
    'invert(50%) sepia(76%) saturate(6994%) hue-rotate(250deg) brightness(79%) contrast(125%)',
};
const btnStyle = {
  textTransform: 'initial',
  fontWeight: 400,
  fontFamily: 'Avenir Roman',
  padding: 0,
  fontSize: 16,
  letterSpacing: '-0.09px',
};

const DropdownMenu = ({
  menuTitle,
  menuItems,
  iconSrc,
  alt,
  divider,
  right,
  labelPrefix,
  menuStyle,
  hasBtnStyle,
  hasChevron,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef();
  const sort = useSelector(state => state.discoverFilters.sort);
  const dispatch = useDispatch();

  const handleCloseMenu = event => {
    if (anchorRef?.current && anchorRef?.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleClose = () => setOpen(false);

  const handleToggle = () => (open ? handleClose() : setOpen(true));

  const renderButton = () => {
    if (iconSrc) {
      return (
        <div ref={anchorRef}>
          <IconButton style={noPaddingRight} onClick={handleToggle} disableRipple>
            <Image src={iconSrc} alt={alt} width={15} height={15} unoptimized />
          </IconButton>
        </div>
      );
    }

    if (menuTitle) {
      return (
        <Button
          ref={anchorRef}
          onClick={handleToggle}
          disableRipple
          className={clsx({ [!hasBtnStyle]: 'hover-icon' })}
          style={isEmpty(btnStyle) ? {} : btnStyle}
          endIcon={
            hasChevron &&
            (open ? (
              <ChevronUpIcon width={12} height={12} style={iconStyle} />
            ) : (
              <ChevronDownIcon width={12} height={12} />
            ))
          }
        >
          {menuTitle}
        </Button>
      );
    }

    return (
      <Button ref={anchorRef} onClick={handleToggle} style={btnStyle} disableRipple>
        <span style={marginRight}>
          {labelPrefix}
          {sort}
        </span>

        {open ? (
          <ChevronUpIcon width={12} height={12} style={iconStyle} />
        ) : (
          <ChevronDownIcon width={12} height={12} />
        )}
      </Button>
    );
  };

  return (
    <div className={classes.root} style={right ? { textAlign: 'right' } : {}}>
      {renderButton()}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        style={zIndex}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper className={classes.paper} style={menuStyle ? menuStyle : {}}>
              <ClickAwayListener onClickAway={handleCloseMenu}>
                <MenuList className={classes.menuList}>
                  {menuItems.map((item, index) => (
                    <div key={index}>
                      <MenuItem
                        onClick={() => {
                          dispatch(setDiscoverSort(item.name.toLowerCase()));
                          if (item.handleClick) item.handleClick();
                          handleClose();
                        }}
                        className={classes.menuListItem}
                      >
                        {item.icon && <span style={spanStyle}>{item.icon}</span>}

                        {item.name || item.title}
                      </MenuItem>

                      {index !== menuItems.length - 1 && divider && <Divider />}
                    </div>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

DropdownMenu.propTypes = {
  menuItems: PropTypes.array.isRequired,
  menuTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  iconSrc: PropTypes.string,
  alt: PropTypes.string,
  divider: PropTypes.bool,
  menuStyle: PropTypes.object,
  right: PropTypes.bool,
  labelPrefix: PropTypes.string,
  hasBtnStyle: PropTypes.bool,
  hasChevron: PropTypes.bool,
  btnStyle: PropTypes.object,
};

DropdownMenu.defaultProps = {
  menuTitle: '',
  iconSrc: '',
  alt: 'menu',
  divider: true,
  menuStyle: {},
  right: false,
  labelPrefix: '',
  hasBtnStyle: false,
  hasChevron: false,
  btnStyle: {},
};

export default DropdownMenu;
